/**
 * @file: measureInWindowAsync.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { delayed } from '@euler/utils';
import { MeasureInWindowOnSuccessCallback } from 'react-native';

export type WindowRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** anything the ui manager can measure — a host view, or a scroll view */
export type MeasurableView = {
  measureInWindow?: (callback: MeasureInWindowOnSuccessCallback) => void;
};

const kMeasureTimeout = 200;

/**
 * promisified `measureInWindow`. resolves with `undefined` when the view is
 * detached (in which case the native callback is never invoked at all).
 */
export async function measureInWindowAsync(
  view: MeasurableView | null | undefined,
): Promise<WindowRect | undefined> {
  if (typeof view?.measureInWindow !== 'function') {
    return undefined;
  }
  return await new Promise<WindowRect | undefined>(resolve => {
    view.measureInWindow!((x, y, width, height) => {
      resolve(Number.isFinite(y) ? { x, y, width, height } : undefined);
    });
    delayed(() => resolve(undefined), kMeasureTimeout);
  });
}
